// 저장된 견적 → 템플릿 공종 트리(「템플릿으로 저장」). 금액·공간·메모는 버리고 이름·단위·수량식만 남긴다.

import { subjectJosa } from "../../lib/josa";
import { quoteAreaM2 } from "../quote/area-unit";
import { trustedQtyExpr } from "../quote/qty-expr-trust";
import { QuoteDetail, QuoteItem } from "../quote/types";
import { TradeCode } from "./trades";
import { TemplateItem } from "./types";

export interface TemplateTradeDraft {
  trade: TradeCode;
  items: TemplateItem[];
}

// 템플릿 이름 상한(templateSaveSchema 와 같다).
const NAME_MAX = 60;

// 템플릿 저장 상한 — templateSaveSchema 가 이 값을 그대로 쓴다(두 벌이면 사전 검사와 스키마가 갈린다).
export const TEMPLATE_LIMITS = {
  scope: 100,
  item: 200,
  unit: 20,
  itemsPerTrade: 300,
  children: 50,
  trades: 40,
} as const;

type TextField = "scope" | "item" | "unit";

// 견적 → 템플릿에서 막히는 까닭. 화면 문구는 templateLimitMessage 가 kind 로 만든다.
export type TemplateLimitIssue =
  | { kind: "unknown_trade"; trade: string; code: string }
  | { kind: "too_many_trades"; count: number; max: number }
  | { kind: "too_many_items"; trade: string; count: number; max: number }
  | { kind: "too_many_children"; trade: string; parent: string; count: number; max: number }
  | { kind: "too_deep"; trade: string; parent: string; item: string }
  | { kind: "too_long"; trade: string; parent: string | null; item: string; field: TextField; length: number; max: number };

const FIELD_LABEL: Record<TextField, string> = { scope: "범위", item: "작업내역", unit: "규격" };

const clip = (text: string): string => (text.length > 20 ? `${text.slice(0, 20)}…` : text);

export const templateLimitMessage = (issue: TemplateLimitIssue): string => {
  switch (issue.kind) {
    case "unknown_trade":
      return `「${issue.trade}」(${issue.code}) 공종이 공종 목록에 없습니다 — 설정 › 공종에서 다시 추가하거나, 견적에서 이 공종을 빼고 저장한 뒤 다시 시도하세요`;
    case "too_many_trades":
      return `공종이 ${issue.count}개입니다 — 템플릿은 ${issue.max}개까지입니다`;
    case "too_many_items":
      return `「${issue.trade}」 공종의 항목이 ${issue.count}개입니다 — 템플릿은 공종당 ${issue.max}개까지입니다`;
    case "too_many_children":
      return `「${issue.trade}」 공종 「${clip(issue.parent)}」 의 SET 자식이 ${issue.count}개입니다 — ${issue.max}개까지입니다`;
    case "too_deep":
      return `「${issue.trade}」 공종 「${clip(issue.parent)}」 아래 「${clip(issue.item)}」 는 3단계입니다 — 템플릿은 SET 부모와 자식 두 단계까지입니다`;
    case "too_long": {
      const where = issue.parent === null ? `「${clip(issue.item)}」` : `「${clip(issue.parent)}」 의 자식 「${clip(issue.item)}」`;
      return `「${issue.trade}」 공종 ${where} — ${FIELD_LABEL[issue.field]}${subjectJosa(FIELD_LABEL[issue.field])} ${issue.length}자입니다(${issue.max}자까지). 견적에서 줄여 저장한 뒤 다시 시도하세요`;
    }
  }
};

// 템플릿 스키마가 원문으로 거절하거나(상한) 조용히 버릴(3단계) 것을 변환 단계에서 먼저 짚는다 — 없으면 null.
export const templateLimitIssueOf = (
  detail: QuoteDetail,
  drafts: TemplateTradeDraft[],
  knownCodes: ReadonlySet<string>
): TemplateLimitIssue | null => {
  const nameOf = (code: string): string =>
    [...detail.trades].sort((a, b) => a.sort_order - b.sort_order).find((t) => t.trade === code)?.name ?? code;
  const unknown = drafts.find((d) => !knownCodes.has(d.trade));
  if (unknown) return { kind: "unknown_trade", trade: nameOf(unknown.trade), code: unknown.trade };
  if (drafts.length > TEMPLATE_LIMITS.trades) {
    return { kind: "too_many_trades", count: drafts.length, max: TEMPLATE_LIMITS.trades };
  }
  const tooLong = (trade: string, parent: string | null, row: TemplateItem): TemplateLimitIssue | null => {
    for (const field of ["scope", "item", "unit"] as const) {
      const length = row[field].length;
      if (length > TEMPLATE_LIMITS[field]) {
        return { kind: "too_long", trade, parent, item: row.item, field, length, max: TEMPLATE_LIMITS[field] };
      }
    }
    return null;
  };
  for (const draft of drafts) {
    const trade = nameOf(draft.trade);
    if (draft.items.length > TEMPLATE_LIMITS.itemsPerTrade) {
      return { kind: "too_many_items", trade, count: draft.items.length, max: TEMPLATE_LIMITS.itemsPerTrade };
    }
    for (const row of draft.items) {
      const own = tooLong(trade, null, row);
      if (own) return own;
      const children = row.children ?? [];
      if (children.length > TEMPLATE_LIMITS.children) {
        return { kind: "too_many_children", trade, parent: row.item, count: children.length, max: TEMPLATE_LIMITS.children };
      }
      for (const child of children) {
        const deep = (child.children ?? [])[0];
        if (deep) return { kind: "too_deep", trade, parent: child.item, item: deep.item };
        const long = tooLong(trade, row.item, child);
        if (long) return long;
      }
    }
  }
  return null;
};

// 「템플릿으로 저장」 을 열지 — 잠긴 견적은 화면 편집을 저장할 길이 없으니 저장본으로 연다(dirty 만이면 먼저 저장하게).
export const templateSaveGateOf = (dirty: boolean, locked: boolean): "ok" | "dirty" | "locked" =>
  !dirty ? "ok" : locked ? "locked" : "dirty";

export const defaultTemplateName = (title: string, isoDate: string): string => {
  const head = title.trim() || "견적";
  return `${head.slice(0, NAME_MAX - isoDate.length - 1).trim()} ${isoDate}`;
};

// 식은 수량을 만들 때만(편집기 toRow 와 같은 trustedQtyExpr) 싣고, 아니면 숫자 수량을 상수식으로 — 0 은 식 없음.
const qtyExprOf = (row: QuoteItem, area: number | null, areaM2: number | null): string | null => {
  const trusted = trustedQtyExpr(row.qty, row.qty_expr, area, areaM2);
  if (trusted) return trusted;
  return row.qty > 0 ? String(row.qty) : null;
};

const bySort = (a: QuoteItem, b: QuoteItem): number => a.sort_order - b.sort_order;

const named = (row: QuoteItem): boolean => row.item.trim() !== "";

export const templateTradesFromQuote = (detail: QuoteDetail): TemplateTradeDraft[] => {
  const area = detail.quote.rough_area_pyeong ?? null;
  const areaM2 = quoteAreaM2(detail.quote);
  const childrenOf = (parentId: string): QuoteItem[] =>
    detail.items.filter((i) => i.parent_id === parentId && named(i)).sort(bySort);

  const toItem = (row: QuoteItem): TemplateItem => {
    const children = childrenOf(row.id).map(toItem);
    const base = { scope: row.scope.trim(), item: row.item.trim(), unit: row.unit.trim() };
    // SET 부모는 수량이 자식 합이라 식을 싣지 않는다.
    if (children.length > 0) return { ...base, children };
    const expr = qtyExprOf(row, area, areaM2);
    return expr ? { ...base, qty_expr: expr } : base;
  };

  // 같은 공종 코드가 두 번 있으면 앞 공종에 모은다 — 템플릿은 공종 코드가 겹치면 저장을 거절한다.
  const out = new Map<string, TemplateTradeDraft>();
  for (const trade of [...detail.trades].sort((a, b) => a.sort_order - b.sort_order)) {
    const roots = detail.items
      .filter((i) => i.quote_trade_id === trade.id && i.parent_id === null && named(i))
      .sort(bySort)
      .map(toItem);
    const prev = out.get(trade.trade);
    if (prev) prev.items.push(...roots);
    else out.set(trade.trade, { trade: trade.trade, items: roots });
  }
  return [...out.values()];
};
